import { createSelector } from 'reselect';
import { capitalizeWord } from '../helpers/stringParsers';
import { getNotHiddenChildren } from '../selectors/children';
import { isApplicantMarried } from '../selectors/applicant';
import { isSpouseRegisteredInMoscow } from '../selectors/spouse';
import {
  isChildBornInCurrentMarriage,
  childAddressMatchesSpouse,
} from '../selectors/child';

import {
  isAdult,
  isUnder23YearsOld,
  shouldBeGettingEducation,
  shouldHaveIdentification,
} from '../helpers/dateHelpers';
import { getValue } from '../constants/getFieldValues';
import {
  DOCUMENT_TYPE,
  DocumentFields,
  PASSPORT_STATUS,
} from '../constants/document';
import { BORN_IN, CHILD_ADDRESS } from '../constants/child';
import { hasErrors } from './index';

interface CardStatusProps {
  cardStatus: { error: string; isCardCompleted: boolean; highlight: boolean };
}

const cardStatusInitial = {
  error: '',
  isCardCompleted: true,
  highlight: false,
};

interface Errors {
  [propName: string]: { error: boolean };
}

export const childrenSelector = (state): Record<string, unknown> =>
  state.children;

const errorMessage = (errors: Errors): string => {
  if (errors?.child?.error) return `missingChildInfo`;
  let error = '';
  const arr = Object.entries(errors).reduce(
    (acc, obj: [string, { error: boolean }]): string[] => {
      if (obj[1].error) {
        const formattedName = capitalizeWord(obj[0]);
        (acc as string[]).push(formattedName);
      }
      return acc;
    },
    []
  );
  if (!arr.length) return '';
  arr.length > 1 ? (error = arr.join('And')) : (error = arr[0]);

  return `missing${error}Info`;
};

const getCardStatus = (errors: Errors): CardStatusProps => {
  const error = errorMessage(errors);
  const highlight = hasErrors(errors);
  const isCardCompleted = !highlight;

  return { cardStatus: { error, highlight, isCardCompleted } };
};

const isChildOutsideMoscow = (child, isSpouseInMoscow): boolean => {
  if (!childAddressMatchesSpouse(child)) return false;
  return childAddressMatchesSpouse(child) && !isSpouseInMoscow;
};
const hasParentsError = ({ bornInCurrentMarriage, secondParent }): boolean => {
  const bornInCurrentMarriageValue = getValue(bornInCurrentMarriage);
  if (bornInCurrentMarriageValue === BORN_IN.currentMarriage) return false;
  return ![
    bornInCurrentMarriageValue === BORN_IN.notCurrentMarriage ||
      secondParent.status.value,
  ].every(Boolean);
};

const hasAddressError = (state): boolean => {
  const {
    addressMatchWithApplicant,
    birthdate,
    address,
    isRegisteredInMoscow,
  } = state;
  const value = getValue(addressMatchWithApplicant);
  if (
    [
      isAdult(getValue(birthdate)),
      value === CHILD_ADDRESS.matchWithSpouse,
      value === CHILD_ADDRESS.matchWithApplicant,
    ].some(Boolean)
  )
    return false;

  return !(value === CHILD_ADDRESS.notMatchWithSpouse
    ? address.isAddressCompleted || !isRegisteredInMoscow
    : value);
};

const isDulCompleted = (dul: DocumentFields) => {
  const { type, number, issuer, issueDate, divisionCode } = dul;
  const typeValue = getValue(type);
  return ![
    typeValue,
    typeValue === DOCUMENT_TYPE.PASSPORT_RF ? getValue(divisionCode) : true,
    getValue(number),
    getValue(issuer),
    getValue(issueDate),
  ].every(Boolean);
};

const hasChildDulError = (state): boolean => {
  const { birthdate, dul } = state;
  return shouldHaveIdentification(getValue(birthdate)) && isDulCompleted(dul);
};

const hasEducationError = (state): boolean => {
  const { birthdate, education, isStudying } = state;
  const birthdateValue = getValue(birthdate);
  if (!isUnder23YearsOld(birthdateValue)) return false;
  const isNotStudying = typeof isStudying === 'boolean' && !isStudying;
  return (
    shouldBeGettingEducation(birthdateValue) &&
    !(getValue(education.institutionName) || isNotStudying)
  );
};

const expiredError = (state): boolean => {
  const { birthdate, dul } = state;
  return (
    shouldHaveIdentification(getValue(birthdate)) &&
    dul.status === PASSPORT_STATUS.EXPIRED
  );
};

// the order matters for the formation of childCardStatus.error
const validators = {
  parents: hasParentsError,
  education: hasEducationError,
  address: hasAddressError,
  child: hasChildDulError,
  passport: expiredError,
};

const validateChildInMoscow = (child, isSpouseInMoscow): CardStatusProps => {
  return {
    cardStatus: isChildOutsideMoscow(child, isSpouseInMoscow)
      ? {
          error: 'notInMoscow',
          highlight: true,
          isCardCompleted: false,
        }
      : { ...cardStatusInitial },
  };
};

export const validateChildrenAreInMoscow = createSelector(
  getNotHiddenChildren,
  isSpouseRegisteredInMoscow,
  (children, isSpouseInMoscow) => {
    return children.reduce((acc, child) => {
      const { id } = child;
      return {
        ...acc,
        [id]: validateChildInMoscow(child, isSpouseInMoscow),
      };
    }, {});
  }
);

const validateChild = (child) => {
  const errors = Object.keys(validators).reduce((acc, key) => {
    return {
      ...acc,
      [key]: { error: validators[key](child) },
    };
  }, {});
  return getCardStatus(errors);
};

export const validateCards = createSelector(
  getNotHiddenChildren,
  (children) => {
    return children.reduce((acc, child) => {
      const { id } = child;
      return {
        ...acc,
        [id]: validateChild(child),
      };
    }, {});
  }
);

export const getChildrenToUpdate = createSelector(
  isApplicantMarried,
  getNotHiddenChildren,
  (isMarried, children) => {
    const needAddress = children.filter(childAddressMatchesSpouse);
    if (isMarried) {
      return {
        parents: children,
        address: needAddress,
      };
    }
    const needParents = children.filter(isChildBornInCurrentMarriage);
    return {
      parents: needParents,
      address: needAddress,
    };
  }
);
